import React from 'react';
import {
  List,
  ListItem,
  ListItemText,
  Box,
  Typography,
  Chip,
  LinearProgress,
} from '@mui/material';
import {
  Timer as TimerIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
} from '@mui/icons-material';

export interface PerformanceMetric {
  id: string;
  name: string;
  callCount: number;
  averageMs: number;
  minMs: number;
  maxMs: number;
  successRate: number;
  successCount?: number;
  failureCount?: number;
  lastCalledAt?: Date;
  metadata?: Record<string, any>;
}

export interface PerformanceListProps {
  metrics: PerformanceMetric[];
  showRank?: boolean;
  showProgress?: boolean;
  maxItems?: number;
  emptyMessage?: string;
  onItemClick?: (metric: PerformanceMetric) => void;
}

export const PerformanceList: React.FC<PerformanceListProps> = ({
  metrics,
  showRank = true,
  showProgress = false,
  maxItems,
  emptyMessage = 'No data available',
  onItemClick,
}) => {
  const displayMetrics = maxItems ? metrics.slice(0, maxItems) : metrics;

  const formatMs = (ms: number) => {
    if (ms < 1000) return `${Math.round(ms)}ms`;
    return `${(ms / 1000).toFixed(2)}s`;
  };

  const getSuccessRateColor = (rate: number): 'success' | 'warning' | 'error' => {
    if (rate >= 95) return 'success';
    if (rate >= 80) return 'warning';
    return 'error';
  };

  const getPerformanceColor = (avgMs: number): 'success' | 'warning' | 'error' => {
    if (avgMs < 100) return 'success';
    if (avgMs < 500) return 'warning';
    return 'error';
  };

  if (displayMetrics.length === 0) {
    return (
      <Typography color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
        {emptyMessage}
      </Typography>
    );
  }

  return (
    <List>
      {displayMetrics.map((metric, index) => (
        <ListItem
          key={metric.id}
          sx={{
            mb: 1,
            bgcolor: 'background.default',
            borderRadius: 1,
            border: 1,
            borderColor: getPerformanceColor(metric.averageMs) === 'error'
              ? 'error.light'
              : 'divider',
            cursor: onItemClick ? 'pointer' : 'default',
            '&:hover': onItemClick ? { bgcolor: 'action.hover' } : {},
          }}
          onClick={() => onItemClick?.(metric)}
        >
          <ListItemText
            primary={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {showRank && (
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 'bold',
                      color: 'text.secondary',
                      minWidth: 24,
                    }}
                  >
                    #{index + 1}
                  </Typography>
                )}
                {getPerformanceColor(metric.averageMs) === 'error' && !showRank && (
                  <ErrorIcon color="error" />
                )}
                <Typography
                  variant="body1"
                  sx={{
                    fontFamily: 'monospace',
                    fontWeight: 600,
                    flex: 1,
                  }}
                >
                  {metric.name}()
                </Typography>
                <Chip
                  label={`${metric.callCount} calls`}
                  color="primary"
                  size="small"
                />
              </Box>
            }
            secondary={
              <Box sx={{ mt: 1 }}>
                {showProgress && (
                  <>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                      <Typography variant="caption">
                        Min: {formatMs(metric.minMs)}
                      </Typography>
                      <Typography variant="caption">
                        Max: {formatMs(metric.maxMs)}
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={Math.min((metric.averageMs / metric.maxMs) * 100, 100)}
                      color={getPerformanceColor(metric.averageMs)}
                      sx={{ height: 6, borderRadius: 1, mb: 0.5 }}
                    />
                  </>
                )}
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <Chip
                    icon={<TimerIcon />}
                    label={`Avg: ${formatMs(metric.averageMs)}`}
                    size="small"
                    variant="outlined"
                    color={getPerformanceColor(metric.averageMs)}
                  />
                  <Chip
                    icon={<CheckCircleIcon />}
                    label={`${metric.successRate}% success`}
                    size="small"
                    variant="outlined"
                    color={getSuccessRateColor(metric.successRate)}
                  />
                  {!showProgress && (
                    <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 2.5 }}>
                      Range: {formatMs(metric.minMs)} - {formatMs(metric.maxMs)}
                    </Typography>
                  )}
                </Box>
              </Box>
            }
          />
        </ListItem>
      ))}
    </List>
  );
};

export default PerformanceList;

